import { Yii } from "libraries/interfaces/Yii";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AbstractControl, FormBuilder, FormControl } from '@angular/forms';

import { GenericFieldset, IGenericFieldset } from '../generic-fieldset.component';
import { ILabels } from "libraries/interfaces/ILabels";
import { ELabelPosition } from "libraries/module/formbuilder/fields/form-field/form-field.component";

interface IQuantity {
    adult: number;
    child: number;
    infant: number;
    rooming: number;
}

/**
 * Quantity-fieldset
 * Generate a fieldset of needed fields
 * @extends GenericFieldset
 */
@Component({
    selector: 'quantity-fieldset',
    templateUrl: './quantity-fieldset.component.html',
    styleUrls: ['./quantity-fieldset.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuantityFieldsetComponent extends GenericFieldset implements IGenericFieldset, OnInit, OnDestroy {
    @Input()
    public readOnlyTravellers: boolean = false;

    @Input()
    public labelPosition: ELabelPosition = ELabelPosition.right;

    /** form hotel title travellers */
    @Input()
    public titleHotelForm: any = false;

    @Input()
    public isFieldFlexFlowRow: boolean = true;

    @Input()
    public wording: ILabels = {
        adt: Yii.t('wui-shared-js', 'ADT'),
        chd: Yii.t('wui-shared-js', 'CHD'),
        inf: Yii.t('wui-shared-js', 'INF'),
        room: Yii.t('wui-shared-js', 'Chambres'),
    };

    public Yii = Yii;


    /** Readonly context can't be modified. Define the context of the fieldset */
    public readonly context: string = 'quantity';
    public rooms: number[] = [1];
    public onRoomingChanges: EventEmitter<any> = new EventEmitter();
    public maxTravellers: {[key: string]: number};
    private groupSubscription: Subscription;
    private MAX_TRAVELLERS_DEFAULT: number = 9;
    /** DI - Formbuilder */
    constructor (public fb: FormBuilder) {
        super(fb);
    }

    public ngOnInit (): void {
        super.ngOnInit();
        this.initMaxTravellers();
        if(this.isAir()) {
            this.initGroupSubscription();
        }
    }

    public ngOnDestroy(): void {
        if (this.groupSubscription) {
            this.groupSubscription.unsubscribe();
        }
    }

    public isAir(): boolean {
        return this.config?.context === 'air';
    }

    private initMaxTravellers(): void {
        this.maxTravellers = {
            adult: this.isAir() ? this.getMaxTravellersByType('adult') : this.MAX_TRAVELLERS_DEFAULT,
            child: this.isAir() ? this.getMaxTravellersByType('child') : this.MAX_TRAVELLERS_DEFAULT,
            infant: this.isAir() ? this.getMaxTravellersByType('infant') : this.MAX_TRAVELLERS_DEFAULT,
        };
    }

    private getMaxTravellersByType(_ctrlName: string): number {
        const adultValue: number = this.group.get('adult').value;
        if(_ctrlName === 'infant'){
            return adultValue;
        }
        if (_ctrlName === 'child') {
            return this.MAX_TRAVELLERS_DEFAULT - adultValue;
        }
        const childValue: number = this.group.get('child').value;
        if (_ctrlName === 'adult') {
            return this.MAX_TRAVELLERS_DEFAULT - childValue;
        }
        return this.MAX_TRAVELLERS_DEFAULT;
    }

    private initGroupSubscription(): void {
        this.groupSubscription = this.group.valueChanges.subscribe((quantity: IQuantity): void => {
            if (quantity.adult > this.maxTravellers['adult']) {
                 this.group.get('adult').setValue(this.maxTravellers['adult']);
            }
            if (quantity.child > this.maxTravellers['child']) {
                this.group.get('child').setValue(this.maxTravellers['child']);
            }
            if (quantity.infant > this.maxTravellers['infant']) {
                this.group.get('infant').setValue(this.maxTravellers['infant']);
            }
            if (quantity.infant > quantity.adult){
                this.group.get('infant').setValue(quantity.adult);
            }
            if ((quantity.child + quantity.adult) === 0) {
                this.group.get('adult').setValue(1);
            }
            this.initMaxTravellers();
        });
    }

    /** Emit action to display dispatching
     * and payload with number of rooms,
     * the number of rooms allows us to add the count
     * of rooms to the model
     */
    private showDispatching (e: Event): void {
        e.preventDefault();
        this.notify.emit({ action: this.actions.TOGGLE_SHOW_DISPATCHING});
    }
}
